/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import axios from 'axios'
import { injectable } from 'inversify'
import { ICoverageDiscoveryRequestApiClient } from './contracts'
import { ICoverageDiscoveryRequestDetail, IStates, ICoverageDiscoveryFacilities, ICoverageDiscoveryResponse } from '../CoverageDiscoveryRequestStateManager/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IUserSessionMiddleware } from '../../arc/middleware/UserSessionMiddleware/contracts'
import { ErrorHelper } from '../../utils/ErrorHelper'

@injectable()
export class CoverageDiscoveryRequestApiClient implements ICoverageDiscoveryRequestApiClient {
	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	saveRequestDetail(RequestDetail: ICoverageDiscoveryRequestDetail): Promise<ICoverageDiscoveryResponse> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/saveRequestDetail`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.post(url, RequestDetail, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}
	getAllFacilities(): Promise<ICoverageDiscoveryFacilities[]> {
		const params = `Limit=${20000}`
		// TODO: the listrequest has a default limit of 20. what if we have a small-ish lookup table but potentially more
		// than to pack into a dropdown list? Need to discuss that detail
		const filterString = '&Active=true'
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerEntityFacilities?${params}${filterString}`
		return this.userSessionMiddleware.loadSessionJwt().then(token =>
			axios
				.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then(response => response.data.items)
				.catch(error => {
					ErrorHelper.handleApiError(error)
				}),
		)
	}
	getAllStates(): Promise<IStates[]> {
		const params = `Limit=${200}`
		// TODO: the listrequest has a default limit of 20. what if we have a small-ish lookup table but potentially more
		// than to pack into a dropdown list? Need to discuss that detail
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/statecodes?${params}`
		return this.userSessionMiddleware.loadSessionJwt().then(token =>
			axios
				.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then(response => response.data.items)
				.catch(error => {
					ErrorHelper.handleApiError(error)
				}),
		)
	}
	getCoverageDiscoveryTransactionStatusId(id: number): Promise<boolean> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/${id}/getResponseStatus`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	updateCDStatusTimeout(id: number): Promise<boolean> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/${id}/updateCDStatusTimeout`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.put(url, id, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	getRequestDetail(id: number): Promise<ICoverageDiscoveryRequestDetail> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/coveragediscoverytransactions/${id}/getRequestDetail`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data.item)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}
}
